/**
 * **通用账户明细抽屉**（批次 `95`）
 *
 * ## 🔴 为什么另做一个，不复用 `ContraTimelineDrawer`
 *
 * 往来账户的时间线里有 **`settlement` 结算行**（本金的唯一痕迹，`DR-047`），
 * 那是往来域独有的语义；预付 / 待摊 / 信用卡没有"结算"这回事。
 * 硬套会让明细里出现一行永远为空的"结算"分类 —— 比没有更糟。
 *
 * ## 口径
 *
 * - 金额取腿的 `amount`（**恒正**）+ `direction`，**服务端不翻符号**；
 * - 分类名由服务端 JOIN 下发（批 `106-A`）—— 端侧**不按 `categoryId` 自己 mapping**（铁律 103）；
 * - 只显示最近 N 条（服务端夹上限并回报 `truncated`），**不静默截断**；
 * - 🆕 **一行 = 银行流水的一笔**（`AC-B2.64`）：服务端已按「同一交易在该账户的同向腿」
 *   合并（`toBillLegRows`）；展开子项只是看"这笔钱怎么分的"，**合计不变**（`AC-B2.39`）。
 */

import type { ReactElement } from 'react';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getAccountLedger } from '../api/endpoints.js';
import { LedgerRowItem } from './LedgerRowItem.js';
import { Loading } from './Loading.js';

export interface AccountLedgerDrawerProps {
  /** `null` = 关闭 */
  accountId: string | null;
  accountName: string;
  currency: string;
  onClose: () => void;
}

export function AccountLedgerDrawer({
  accountId,
  accountName,
  currency,
  onClose,
}: AccountLedgerDrawerProps): ReactElement {
  const open = accountId !== null;
  const [openTxn, setOpenTxn] = useState<string | null>(null);
  const ledger = useQuery({
    queryKey: ['accountLedger', accountId ?? ''],
    queryFn: () => getAccountLedger(accountId as string),
    enabled: open,
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} data-testid="account-ledger-drawer">
      <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {accountName} · 明细
        </Typography>
        <Typography variant="caption" color="text.secondary">
          这个账户上每一笔钱进出的记录（含转账的另一端）
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {ledger.isPending && <Loading py={3} />}
        {ledger.isError && (
          <Alert severity="error">明细读取失败：{String(ledger.error)}</Alert>
        )}
        {ledger.isSuccess && ledger.data.items.length === 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="ledger-empty">
            这个账户还没有任何流水。
          </Typography>
        )}
        {ledger.isSuccess && ledger.data.items.length > 0 && (
          <Stack spacing={1.25} data-testid="ledger-list">
            {ledger.data.items.map((row, idx) => {
              const key = `${row.transactionId}|${row.direction}`;
              return (
                <LedgerRowItem
                  key={`${row.transactionId}-${row.direction}-${row.date}`}
                  row={row}
                  currency={currency}
                  expanded={openTxn === key}
                  onToggle={() => {
                    setOpenTxn(openTxn === key ? null : key);
                  }}
                  // 最后一行不画分隔线（避免抽屉底部多一条悬空的线）
                  withDivider={idx < ledger.data.items.length - 1}
                />
              );
            })}
          </Stack>
        )}

        {ledger.isSuccess && ledger.data.truncated && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            只显示最近 {ledger.data.count} 条 —— 更早的请到「流水」页按账户筛选。
          </Typography>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button size="small" onClick={onClose}>
            关闭
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
